'use client';

import { useEffect, useRef, useState, type ComponentProps } from 'react';
import type { ModelAnnotation, RotationLimits, SceneConfig, SceneLoadProgress, SceneSettings, ViewerController } from '@/shared/config/splat-scene';
import styles from './viewer.module.css';
export { ModelLoader } from './loader';

type Props = Omit<ComponentProps<'div'>, 'onError' | 'onProgress'> & {
  endpoint: string;
  settings?: SceneSettings;
  annotations?: ModelAnnotation[];
  edit?: boolean;
  unclipped?: boolean;
  rotationLimits?: RotationLimits;
  suspended?: boolean;
  hostRef?: (node: HTMLDivElement | null) => void;
  onController?: (controller: ViewerController | null) => void;
  onReady?: () => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
  onReturnReady?: () => void;
  onProgress?: (progress: SceneLoadProgress) => void;
};

/** One renderer for cards, standalone links and the CMS scene editor. */
export function SplatSurface({ endpoint, settings, annotations, rotationLimits, edit = false, unclipped = false, suspended = false, hostRef, onController, onReady, onError, onClose, onReturnReady, onProgress, className, ...props }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const controller = useRef<ViewerController | null>(null);
  const latest = useRef({ settings, annotations, rotationLimits, suspended, onController, onReady, onError, onClose, onReturnReady, onProgress });
  useEffect(() => { latest.current = { settings, annotations, rotationLimits, suspended, onController, onReady, onError, onClose, onReturnReady, onProgress }; });
  const [error, setError] = useState('');
  useEffect(() => {
    const abort = new AbortController();
    const clearError = requestAnimationFrame(() => setError(''));
    latest.current.onController?.(null);
    let api: ViewerController | undefined;
    const run = async () => {
      try {
        const response = await fetch(endpoint, { signal: abort.signal });
        const config: SceneConfig & { error?: string } = await response.json();
        if (!response.ok) throw new Error(config.error || 'Сцена недоступна');
        if (latest.current.annotations) config.presentation = { ...config.presentation, annotations: latest.current.annotations };
        if (latest.current.settings) config.settings = latest.current.settings;
        if (latest.current.rotationLimits) config.presentation = { ...config.presentation, rotationLimits: latest.current.rotationLimits };
        const moduleURL = new URL('/splat-viewer/renderer.js?v=20261009-webp-crops-1', location.origin).href;
        const { mountSplatViewer } = await import(/* webpackIgnore: true */ /* turbopackIgnore: true */ moduleURL);
        if (abort.signal.aborted || !host.current) return;
        api = await mountSplatViewer(host.current, config, {
          signal: abort.signal, edit, unclipped,
          onClose: latest.current.onClose ? () => latest.current.onClose?.() : undefined,
          onReturnReady: () => latest.current.onReturnReady?.(),
          onProgress: (progress: SceneLoadProgress) => { if (!abort.signal.aborted) latest.current.onProgress?.(progress); },
        });
        if (abort.signal.aborted) { api?.destroy(); return; }
        controller.current = api!;
        if (latest.current.annotations) api!.setAnnotations(latest.current.annotations);
        if (latest.current.settings) api!.updateSettings(latest.current.settings);
        if (latest.current.rotationLimits) api!.setRotationLimits(latest.current.rotationLimits);
        if (latest.current.suspended) api!.suspend();
        latest.current.onController?.(api!);
        latest.current.onReady?.();
      } catch (cause) {
        if (abort.signal.aborted) return;
        const failure = cause instanceof Error ? cause : new Error('Не удалось загрузить модель');
        setError(failure.message); latest.current.onError?.(failure);
      }
    };
    void run();
    return () => {
      cancelAnimationFrame(clearError); abort.abort(); api?.destroy(); controller.current = null;
      latest.current.onController?.(null);
    };
  }, [endpoint, edit, unclipped]);
  useEffect(() => { if (annotations) controller.current?.setAnnotations(annotations); }, [annotations]);
  useEffect(() => { if (settings) controller.current?.updateSettings(settings); }, [settings]);
  useEffect(() => { if (rotationLimits) controller.current?.setRotationLimits(rotationLimits); }, [rotationLimits]);
  useEffect(() => { if (suspended) controller.current?.suspend(); else void controller.current?.resume().catch(() => {}); }, [suspended]);
  return <div {...props} data-unclipped={unclipped} ref={node => {
    host.current = node;
    hostRef?.(node);
  }} className={`${styles.surface} ${className || ''}`}>
    {error && <div role="alert" className={styles.status}>{error}</div>}
  </div>;
}
